// Fetch a single asset (with photos) in the shape `AssetDetailPanel` expects.
// Mirrors the structure built by `usePropertyAssetRegistry` for one row.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import type { Asset, AssetPhoto } from "./usePropertyAssetRegistry";

const ASSET_COLUMNS = `
  id, property_id, name, manufacturer, model_number, model_name, serial_number, status, category_id,
  asset_categories (
    name
  ),
  manufacture_date_string, estimated_age_years, age_source,
  asset_photo_url, nameplate_photo_url,
  category_attributes, processing_status, condition_status,
  ai_failure_reason, ai_raw_response, qr_code_id, notes
`;

export function useAssetById(assetId: string | null | undefined) {
  return useQuery<Asset | null>({
    queryKey: ["asset-by-id", assetId],
    enabled: Boolean(assetId),
    queryFn: async () => {
      if (!assetId) return null;

      const [assetRes, photosRes] = await Promise.all([
        supabase.from("property_assets").select(ASSET_COLUMNS).eq("id", assetId).maybeSingle(),
        supabase
          .from("asset_documents")
          .select("id, file_path, document_name")
          .is("deleted_at", null)
          .eq("asset_id", assetId)
          .eq("document_type", "photo"),
      ]);

      if (assetRes.error) throw assetRes.error;
      if (!assetRes.data) return null;

      const asset = assetRes.data as any;
      const categoryData = Array.isArray(asset.asset_categories)
        ? asset.asset_categories[0]
        : asset.asset_categories;
      const categoryName = categoryData?.name || 'Uncategorized';

      const photos: AssetPhoto[] = (photosRes.data ?? [])
        .filter((p): p is { id: string; file_path: string; document_name: string } => !!p.file_path)
        .map((p) => ({
          id: p.id,
          file_path: p.file_path,
          document_name: p.document_name,
        }));

      const fallbackUrl = asset.asset_photo_url || asset.nameplate_photo_url;
      if (photos.length === 0 && fallbackUrl) {
        photos.push({
          id: `primary-${asset.id}`,
          file_path: fallbackUrl,
          document_name: 'Primary Image'
        });
      }

      const attrs = (asset.category_attributes as any) || {};

      return {
        ...asset,
        category: categoryName,
        asset_type: categoryName,
        condition: asset.condition_status || 'Good',
        primary_image_path: fallbackUrl || '',

        voltage: attrs.voltage?.toString() || null,
        amperage: attrs.amperage?.toString() || null,
        phase: attrs.phase || null,
        wires: attrs.wires || null,
        btu_capacity: attrs.btu_capacity?.toString() || null,
        capacity_gallons: attrs.gallon_capacity != null ? Number(attrs.gallon_capacity) : null,
        pressure_psi: attrs.pressure_psi != null ? Number(attrs.pressure_psi) : null,
        frequency_hz: attrs.frequency_hz != null ? Number(attrs.frequency_hz) : null,
        power_kw: attrs.power_kw != null ? Number(attrs.power_kw) : null,
        efficiency_rating: attrs.seer_eer?.toString() || attrs.efficiency_rating?.toString() || null,
        fuel_type: attrs.fuel_type || attrs.generator_fuel_type || null,
        airflow_cfm: attrs.airflow_cfm != null ? Number(attrs.airflow_cfm) : null,
        refrigerant_type: attrs.refrigerant_type || null,
        size_tons: attrs.tonnage != null ? Number(attrs.tonnage) : null,

        photos,
      } as Asset;
    },
  });
}
